import type { TablePager } from '@fukuri-admins/ui/common';
import {
  Button,
  ButtonLink,
  DownloadIcon,
  PlusIcon,
  Stack,
  TablePager as TablePagerComponent,
  TextInputField,
} from '@fukuri-admins/ui/common';
import styles from './styles.module.scss';
import { UsersTable } from './UsersTable';

type Props = {
  /** テーブルに表示する利用者行データ */
  rows: React.ComponentProps<typeof UsersTable>['rows'];
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onEditUser: (userId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onDeleteUser: (userId: number) => void;
  /**
   * 行クリック時のコールバック（利用者詳細画面への遷移）
   *
   * @param userId 操作対象の利用者 ID
   */
  onRowClick: (userId: number) => void;
  /** ページャー props */
  pagerProps: React.ComponentProps<typeof TablePager>;
  /** 名前検索フォームの値 */
  fullNameValue: string;
  /** メールアドレス検索フォームの値 */
  emailValue: string;
  /** 会社名検索フォームの値 */
  clientNameValue: string;
  /**
   * 名前検索フォーム変更時のコールバック
   *
   * @param value 入力値
   */
  onFullNameChange: (value: string) => void;
  /**
   * メールアドレス検索フォーム変更時のコールバック
   *
   * @param value 入力値
   */
  onEmailChange: (value: string) => void;
  /**
   * 会社名検索フォーム変更時のコールバック
   *
   * @param value 入力値
   */
  onClientNameChange: (value: string) => void;
  /** 検索ボタン押下時のコールバック */
  onSearch: () => void;
  /** 条件をリセットボタン押下時のコールバック */
  onReset: () => void;
  /** CSV ダウンロードボタン押下時のコールバック */
  onDownloadCsv: () => void;
  /** CSV ダウンロード中フラグ */
  isDownloadingCsv: boolean;
};

/**
 * 利用者一覧（BankAccount）Presentation
 *
 * - ContentsHeader に「新規追加」ボタンを配置する（title/description は Screen 層の PageHeader が担う）
 * - 検索フォーム: 名前・メールアドレス・会社名
 * - UsersTable にテーブル本体を委譲し、TablePager を直後に配置する（gap=16 で並ぶ）
 * - 利用開始日・更新日時列は常時表示する
 */
export const UserListBankAccountPresentation: React.FC<Props> = ({
  rows,
  onEditUser,
  onDeleteUser,
  onRowClick,
  pagerProps,
  fullNameValue,
  emailValue,
  clientNameValue,
  onFullNameChange,
  onEmailChange,
  onClientNameChange,
  onSearch,
  onReset,
  onDownloadCsv,
  isDownloadingCsv,
}) => (
  <div data-testid='user-list-bank-account'>
    <Stack direction='column' gap={16}>
      <div className={styles.actions}>
        <Stack direction='row' gap={8}>
          <Button
            variant='outline'
            size='md'
            type='button'
            icon={<DownloadIcon size='xs' />}
            iconPosition='left'
            onClick={onDownloadCsv}
            disabled={isDownloadingCsv}
          >
            CSVダウンロード
          </Button>
          <ButtonLink
            variant='primary'
            size='md'
            icon={<PlusIcon size='xs' />}
            iconPosition='left'
            to='/users/new'
          >
            新規追加
          </ButtonLink>
        </Stack>
      </div>
      <Stack direction='column' gap={8}>
        <Stack direction='row' gap={8}>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-bank-account-full-name-input',
                labelText: '名前',
                errorMessage: null,
              }}
              textInput={{
                placeholder: '名前を入力',
                value: fullNameValue,
                onChange: e => onFullNameChange(e.target.value),
              }}
            />
          </div>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-bank-account-email-input',
                labelText: 'メールアドレス',
                errorMessage: null,
              }}
              textInput={{
                placeholder: 'メールアドレスを入力',
                value: emailValue,
                onChange: e => onEmailChange(e.target.value),
              }}
            />
          </div>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-bank-account-client-name-input',
                labelText: '会社名',
                errorMessage: null,
              }}
              textInput={{
                placeholder: '会社名を入力',
                value: clientNameValue,
                onChange: e => onClientNameChange(e.target.value),
              }}
            />
          </div>
        </Stack>
        <Stack direction='row' gap={8}>
          <Button variant='secondary' type='button' onClick={onSearch}>
            検索
          </Button>
          <Button variant='ghost' type='button' onClick={onReset}>
            条件をリセット
          </Button>
        </Stack>
      </Stack>
      <UsersTable
        rows={rows}
        onEditUser={onEditUser}
        onDeleteUser={onDeleteUser}
        onRowClick={onRowClick}
      />
      <TablePagerComponent {...pagerProps} />
    </Stack>
  </div>
);
